'use client';

import { useMemo } from 'react';
import { ArrowUpDownIcon, Tick01Icon } from '@terra/ui-icons';
import { VStack } from '@terra/ui-styled-system';
import { AlertBanner } from '../../../components/alert-banner';
import { Field } from '../../../components/field';
import { Select, createSelectListCollection } from '../../../components/select';
import { Spinner } from '../../../components/spinner';
import type { ExternalNetworkSettingsSelectItem } from './external-settings.model';

export type InternalNetworkSelectFieldLabels = {
  networkLabel: string;
  networkHelper: string;
  networkPlaceholder: string;
  noNetworksAvailable: string;
  formatSingleNetwork: (name: string) => string;
};

export type InternalNetworkSelectFieldProps = {
  items: readonly ExternalNetworkSettingsSelectItem[];
  value: string;
  onValueChange: (value: string) => void;
  isLoading: boolean;
  disabled?: boolean;
  labels: InternalNetworkSelectFieldLabels;
};

export function InternalNetworkSelectField({
  items,
  value,
  onValueChange,
  isLoading,
  disabled,
  labels,
}: InternalNetworkSelectFieldProps) {
  const collection = useMemo(
    () =>
      createSelectListCollection({
        items: [...items],
        itemToValue: (item) => item.value,
        itemToString: (item) => item.label,
      }),
    [items],
  );
  const [singleItem] = items;

  if (isLoading) {
    return <Spinner size="sm" />;
  }

  if (!singleItem) {
    return (
      <AlertBanner
        kind="info"
        type="outline"
        description={labels.noNetworksAvailable}
      />
    );
  }

  if (items.length === 1) {
    return (
      <AlertBanner
        kind="info"
        type="outline"
        description={labels.formatSingleNetwork(singleItem.label)}
      />
    );
  }

  return (
    <VStack gap={3} alignItems="stretch" width="full">
      <AlertBanner
        kind="info"
        type="outline"
        description={labels.networkHelper}
      />
      <Field.Root width="full">
        <Field.Label fontWeight="normal">{labels.networkLabel}</Field.Label>
        <Select.Root
          collection={collection}
          value={value ? [value] : []}
          onValueChange={({ value: selectedValues }) => {
            onValueChange(selectedValues[0] ?? '');
          }}
          disabled={disabled}
          size="sm"
          positioning={{
            sameWidth: true,
            strategy: 'fixed',
            placement: 'bottom-start',
          }}
          width="full"
        >
          <Select.Control w="full">
            <Select.Trigger aria-label={labels.networkLabel}>
              <Select.ValueText placeholder={labels.networkPlaceholder} />
              <ArrowUpDownIcon aria-hidden="true" />
            </Select.Trigger>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              {collection.items.map((item) => (
                <Select.Item key={item.value} item={item}>
                  <Select.ItemText fontSize="xs">{item.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <Tick01Icon size={14} />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      </Field.Root>
    </VStack>
  );
}
